/**
 * AuthStateManager - Single source of truth for authentication state
 * 
 * Coordinates between:
 * - TokenService (token expiry tracking)
 * - SecureAuthService (auth operations)
 * - Redux store (UI state)
 * - RTK Query (API cache)
 * - WebSocket (real-time connection)
 */

import { EventEmitter } from 'events';
import { setUserSession } from '@/core/store/slices/userProfileSlice';
import { tokenService } from './TokenService';
import { secureAuthService } from './SecureAuthService';
import { authLogger } from '@/core/utils/logger';

// Lazy imports to avoid circular dependencies
// Using dynamic import instead of require to satisfy ESLint
import type { Store } from '@reduxjs/toolkit';

interface BaseApiType {
  util: {
    resetApiState: () => { type: string };
  };
}

let storeInstance: Store | null = null;
let baseApiInstance: BaseApiType | null = null;

// Initialize modules asynchronously
(async () => {
  if (typeof window !== 'undefined') {
    const [storeModule, baseApiModule] = await Promise.all([
      import('@/core/store'),
      import('@/api/core/baseApi')
    ]);
    storeInstance = storeModule.store as Store;
    baseApiInstance = baseApiModule.baseApi;
  }
})();

const getStore = () => {
  if (!storeInstance) {
    // Store not yet initialized - this can happen during app startup
    // Return null and let callers handle gracefully
    return null;
  }
  return storeInstance;
};

const getBaseApi = () => {
  if (!baseApiInstance) {
    // BaseApi not yet initialized - this can happen during app startup
    // Return null and let callers handle gracefully
    return null;
  }
  return baseApiInstance;
};

export interface AuthState {
  isAuthenticated: boolean;
  isRefreshing: boolean;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    organizationId: string;
  } | null;
  session: {
    expiresAt: number | null;
    isExpiring: boolean;
    timeUntilExpiry: number | null;
  };
  lastActivity: number;
  lastRefresh: number | null;
}

class AuthStateManager extends EventEmitter {
  private static instance: AuthStateManager;
  private state: AuthState;
  private stateCheckInterval: NodeJS.Timeout | null = null;
  private boundUpdateActivity: () => void;
  private boundBeforeUnload: () => void;
  private isDestroyed: boolean = false;

  private readonly ACTIVITY_CHECK_INTERVAL = 30000; // Check every 30 seconds
  private readonly IDLE_TIMEOUT = 14400000; // 4 hours

  private constructor() {
    super();
    // Prevent memory leaks from unbounded listeners
    this.setMaxListeners(15);
    // Bind methods once to ensure proper cleanup
    this.boundUpdateActivity = this.updateActivity.bind(this);
    this.boundBeforeUnload = this.handleBeforeUnload.bind(this);

    this.state = {
      isAuthenticated: false,
      isRefreshing: false,
      user: null,
      session: {
        expiresAt: null,
        isExpiring: false,
        timeUntilExpiry: null,
      },
      lastActivity: Date.now(),
      lastRefresh: null,
    };

    this.setupEventListeners();
    this.startStateMonitoring();
  }

  /**
   * Handle page unload - cleanup resources
   */
  private handleBeforeUnload(): void {
    this.destroy();
  }
  
  static getInstance(): AuthStateManager {
    if (!AuthStateManager.instance) {
      AuthStateManager.instance = new AuthStateManager();
    }
    return AuthStateManager.instance;
  }
  
  /**
   * Set up event listeners for auth components
   */
  private setupEventListeners(): void {
    tokenService.on('token:refreshed', () => {
      this.updateSessionState();
      this.emit('session:refreshed');
    });
    
    tokenService.on('token:expiring', () => {
      this.state.session.isExpiring = true;
      this.emit('session:expiring');
      this.notifyStateChange();
    });
    
    tokenService.on('token:expired', () => {
      this.handleSessionExpired();
    });
    
    tokenService.on('token:cleared', () => {
      this.handleLogout();
    });
    
    // Track user activity using bound method for proper cleanup
    if (typeof window !== 'undefined') {
      ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(event => {
        window.addEventListener(event, this.boundUpdateActivity, { passive: true });
      });

      // Clean up on page unload to prevent memory leaks
      window.addEventListener('beforeunload', this.boundBeforeUnload);
    }
  }
  
  /**
   * Start monitoring auth state
   */
  private startStateMonitoring(): void {
    this.stateCheckInterval = setInterval(() => {
      this.checkAuthState();
    }, this.ACTIVITY_CHECK_INTERVAL);
    
    // Initial check
    this.checkAuthState();
  }
  
  /**
   * Check current auth state and update
   */
  private checkAuthState(): void {
    const _tokenStatus = tokenService.getStatus();
    const isAuthenticated = secureAuthService.isAuthenticated();

    if (this.state.isAuthenticated !== isAuthenticated) {
      this.state.isAuthenticated = isAuthenticated;
      
      if (isAuthenticated) {
        this.syncUserFromStore();
      } else {
        this.state.user = null;
      }
    }

    this.updateSessionState();

    if (isAuthenticated && this.isIdle()) {
      authLogger.warn('[AuthStateManager] User idle for too long');
      // Don't auto-logout, but mark session as expiring
      this.state.session.isExpiring = true;
    }
    
    this.notifyStateChange();
  }
  
  /**
   * Update session state from token service
   */
  private updateSessionState(): void {
    const tokenStatus = tokenService.getStatus();
    
    this.state.session = {
      expiresAt: tokenStatus.timeUntilExpiry 
        ? Date.now() + (tokenStatus.timeUntilExpiry * 1000)
        : null,
      isExpiring: tokenStatus.isExpiring,
      timeUntilExpiry: tokenStatus.timeUntilExpiry,
    };
    
    if (tokenStatus.hasRefreshInProgress) {
      this.state.isRefreshing = true;
    } else {
      this.state.isRefreshing = false;
    }
  }
  
  /**
   * Sync user data from Redux store
   */
  private syncUserFromStore(): void {
    const store = getStore();
    if (!store) {
      // Store not ready yet, skip sync
      return;
    }
    
    const reduxState = store.getState();
    const userProfile = (reduxState as { userProfile?: { session?: { id: string; email?: string; fullName?: string; organizationId?: string } | null } }).userProfile?.session;

    if (userProfile) {
      this.state.user = {
        id: userProfile.id,
        email: userProfile.email || '',
        firstName: userProfile.fullName?.split(' ')[0] || '',
        lastName: userProfile.fullName?.split(' ').slice(1).join(' ') || '',
        organizationId: userProfile.organizationId || '',
      };
    }
  }
  
  /**
   * Update last activity timestamp
   */
  private updateActivity = (): void => {
    this.state.lastActivity = Date.now();
  };
  
  /**
   * Check if user is idle
   */
  private isIdle(): boolean {
    return Date.now() - this.state.lastActivity > this.IDLE_TIMEOUT;
  }
  
  /**
   * Handle session expired
   */
  private handleSessionExpired(): void {
    authLogger.info('[AuthStateManager] Session expired');
    
    this.state.isAuthenticated = false;
    this.state.session.isExpiring = false;
    this.state.session.expiresAt = null;
    
    this.emit('session:expired');
    this.notifyStateChange();
  }
  
  /**
   * Clear all authentication state comprehensively
   * This is the single point of truth for auth cleanup
   */
  public async clearAllAuthState(): Promise<void> {
    authLogger.info('[AuthStateManager] Clearing all auth state');

    // 1. Reset internal state
    this.state = {
      isAuthenticated: false,
      isRefreshing: false,
      user: null,
      session: {
        expiresAt: null,
        isExpiring: false,
        timeUntilExpiry: null,
      },
      lastActivity: Date.now(),
      lastRefresh: null,
    };

    // 2. Clear Redux state (if store is ready)
    const store = getStore();
    if (store) {
      store.dispatch(setUserSession(null));

      // Clear all RTK Query cache
      const baseApi = getBaseApi();
      if (baseApi) {
        store.dispatch(baseApi.util.resetApiState());
      }
    }

    // 3. Clear sessionStorage auth-related items
    if (typeof window !== 'undefined') {
      try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && (
            key.includes('auth') ||
            key.includes('session') ||
            key.includes('csrf') ||
            key.includes('e2e_mock')
          )) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => sessionStorage.removeItem(key));
        authLogger.info('[AuthStateManager] Cleared sessionStorage items:', keysToRemove);
      } catch (e) {
        authLogger.warn('[AuthStateManager] Failed to clear sessionStorage:', e);
      }
    }

    // 4. Clear CSRF cache (this also clears the cookie and init promise)
    try {
      const { clearCsrfCache } = await import('@/api/core/baseQueryWithRetry');
      clearCsrfCache();
    } catch (e) {
      authLogger.warn('[AuthStateManager] Failed to clear CSRF cache:', e);
    }

    authLogger.info('[AuthStateManager] All auth state cleared');
  }

  /**
   * Handle logout
   */
  private async handleLogout(): Promise<void> {
    authLogger.info('[AuthStateManager] User logged out');

    await this.clearAllAuthState();

    this.emit('auth:unauthenticated');
    this.notifyStateChange();
  }
  
  /**
   * Notify listeners of state change
   */
  private notifyStateChange(): void {
    this.emit('state:changed', this.getState());
  }
  
  /**
   * Get current auth state
   */
  getState(): AuthState {
    return { ...this.state };
  }
  
  /**
   * Set authenticated user
   */
  setAuthenticatedUser(user: AuthState['user']): void {
    authLogger.info('[AuthStateManager] Setting authenticated user', { userId: user?.id });
    
    this.state.isAuthenticated = true;
    this.state.user = user;
    this.state.lastRefresh = Date.now();
    
    // Sync to Redux (if store is ready)
    const store = getStore();
    if (user && store) {
      store.dispatch(setUserSession({
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        organizationId: user.organizationId,
      }));
    }
    
    this.emit('auth:authenticated', user);
    this.notifyStateChange();
  }
  
  /**
   * Trigger auth refresh
   */
  async refreshAuth(): Promise<void> {
    if (this.state.isRefreshing) {
      authLogger.info('[AuthStateManager] Refresh already in progress');
      return;
    }
    
    this.state.isRefreshing = true;
    this.notifyStateChange();
    
    try {
      await secureAuthService.refreshAccessToken();
      this.state.lastRefresh = Date.now();
      this.updateSessionState();
      authLogger.info('[AuthStateManager] Auth refresh successful');
    } catch (error) {
      authLogger.error('[AuthStateManager] Auth refresh failed', error);
      throw error;
    } finally {
      this.state.isRefreshing = false;
      this.notifyStateChange();
    }
  }
  
  /**
   * Check if auth refresh is needed
   */
  shouldRefresh(): boolean {
    if (!this.state.isAuthenticated) return false;
    if (this.state.isRefreshing) return false;
    
    const tokenStatus = tokenService.getStatus();
    
    // Refresh if expiring soon
    if (tokenStatus.isExpiring) return true;
    
    // Refresh if last refresh was over 3 hours ago
    if (this.state.lastRefresh) {
      const hoursSinceRefresh = (Date.now() - this.state.lastRefresh) / (1000 * 60 * 60);
      if (hoursSinceRefresh > 3) return true;
    }
    
    return false;
  }
  
  /**
   * Clean up resources
   */
  destroy(): void {
    // Prevent double cleanup
    if (this.isDestroyed) {
      return;
    }
    this.isDestroyed = true;

    if (this.stateCheckInterval) {
      clearInterval(this.stateCheckInterval);
      this.stateCheckInterval = null;
    }

    // Remove all window event listeners using the same bound references
    if (typeof window !== 'undefined') {
      ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(event => {
        window.removeEventListener(event, this.boundUpdateActivity);
      });
      window.removeEventListener('beforeunload', this.boundBeforeUnload);
    }

    this.removeAllListeners();
    authLogger.info('[AuthStateManager] Destroyed and cleaned up listeners');
  }
}

// Export singleton instance
export const authStateManager = AuthStateManager.getInstance();